// از دادهٔ خامِ `admin_alias_entries` (مایگریشن 0152) تا جدولِ واژه‌نامه‌ی آدرس
// در سربرگِ «آدرس‌ها» (WHOSUP-245).
//
// ═══ چرا این فایل ستون‌ها را «تشخیص» می‌دهد ═══
//
// `crawler.alias_entries` مالِ مخزنِ event_crawler است و ستون‌هایش همان‌جا
// تعریف می‌شوند. SQL ردیف را کامل (`to_jsonb`) می‌فرستد و این‌جا اسمِ هیچ
// ستونی حدس زده نمی‌شود جز دو شمارنده‌ای که قرارِ کرالر است اضافه کند
// (`times_used`، `last_used_at` — `plan/prompts/crawler-alias-usage.md`).
// حکم و «چه کسی تصمیم گرفت» از روی **مقدار** پیدا می‌شوند: ستونی که همه‌ی
// مقدارهای پُرش در {same, narrower, different} است حکم است، و ستونی که در
// {human, seed, model} است منشأِ حکم. این‌طوری اگر کرالر اسمِ ستونی را عوض
// کند، صفحه با آن جابه‌جا می‌شود و بی‌صدا خالی نمی‌ماند.

export type AliasRowRaw = {
  /** کلیدِ اصلیِ ردیف؛ `null` یعنی جدول کلیدِ اصلی ندارد و کلید قفل است. */
  key: string | null;
  row: Record<string, unknown>;
  /** `null` = ادمین هیچ‌وقت دست نزده، یعنی روشن (پیش‌فرض). */
  enabled: boolean | null;
  switched_at: string | null;
};

export type AliasEntriesData = {
  exists: boolean;
  key_columns: string[];
  total: number;
  rows: AliasRowRaw[];
  switches: number;
  orphan_switches: number;
};

/** جوابِ `admin_alias_usage` (مایگریشن 0156): استفاده‌های یک جفت. */
export type AliasUsageData = {
  /** `false` یعنی این دیتابیس جدولِ `crawler.alias_usage` را ندارد. */
  exists: boolean;
  total: number;
  /** display / identity → شمار. */
  by_lens: Record<string, number>;
  rows: {
    lens: string | null;
    subject: string | null;
    first_used_at: string | null;
    last_used_at: string | null;
  }[];
};

export const VERDICTS = ['same', 'narrower', 'different'] as const;
export type Verdict = typeof VERDICTS[number];

export const VERDICT_LABEL: Record<Verdict | 'none', string> = {
  same: 'یکی‌اند',
  narrower: 'جزئی‌تر',
  different: 'فرق دارند',
  none: 'بی‌حکم',
};

export const VERDICT_HELP: Record<Verdict | 'none', string> = {
  same: 'دو طرف یک چیزند و هر کدام می‌تواند جای دیگری بنشیند (street = st).',
  narrower: 'یک طرف بخشی از دیگری است — نه برعکس.',
  different: 'شبیه‌اند ولی یکی نیستند؛ این ردیف جلوی یکی کردنِ اشتباه را می‌گیرد.',
  none: 'هنوز نه آدم و نه فایلِ seed درباره‌اش تصمیم نگرفته.',
};

export const DECIDERS = ['human', 'seed', 'model'] as const;
export type Decider = typeof DECIDERS[number];

export const DECIDER_LABEL: Record<Decider, string> = {
  human: 'آدم', seed: 'فایلِ seed', model: 'مدل',
};

/** ستون‌هایی که اسمشان قرارداد است (سهمِ کرالر). */
export const USAGE_COL = 'times_used';
export const LAST_USED_COL = 'last_used_at';

/**
 * ═══ جفتِ تک‌تکه در برابرِ آدرسِ کامل (WHOSUP-266) ═══
 *
 * جدول دو جور ردیف دارد و قاطیِ هم بی‌معنی بودند: «court = ct» (خانواده‌ی
 * `phrase`) واژه است؛ «1961 mccreary rd, winnipeg, manitoba = … mb»
 * (`full_value`) دو آدرسِ کامل است که کرالر یکی دانسته — یعنی **مثال**. پس
 * فهرستِ اصلی فقط واژه‌هاست و آدرس‌های کامل زیرِ جفتی می‌آیند که همان
 * تفاوت را دارند.
 *
 * این چهار اسم قرارداد است و از جدولِ کرالر آمده، نه حدس: `family` با دو
 * مقدارش، و دو طرفِ خام `a_raw`/`b_raw` — خام و نه `a`/`b`ِ یکسان‌شده، چون
 * یکسان‌سازی دو طرفِ «court = ct» را هر دو «ct» می‌کند و جفت را نامرئی.
 * اگر `family` نباشد، همه‌ی ردیف‌ها جفت‌اند و مثالی در کار نیست — همان رفتارِ
 * پیش از این تیکت.
 */
export const FAMILY_COL = 'family';
export const FAMILY_PHRASE = 'phrase';
export const FAMILY_FULL = 'full_value';
export const SIDE_A_COL = 'a_raw';
export const SIDE_B_COL = 'b_raw';
/** همان دو طرف، یکسان‌شده؛ در جدول با مقدارِ خام پر می‌شوند (WHOSUP-266). */
export const NORM_A_COL = 'a';
export const NORM_B_COL = 'b';
/** کلیدِ اتصال به `crawler.alias_usage` (مایگریشن 0156). */
export const RAW_HASH_COL = 'raw_hash';
/** طرفِ دومِ یک جفتِ «اختیاری»: یعنی طرفِ اول را می‌شود انداخت. */
export const EMPTY_SIDE = '∅';

export type AliasRow = {
  key: string | null;
  enabled: boolean;
  /** ادمین صریحاً دست زده (روشن یا خاموش)، نه پیش‌فرض. */
  switched: boolean;
  switchedAt: string | null;
  verdict: Verdict | null;
  decider: Decider | null;
  /** `null` یعنی کرالر هنوز شمارنده ندارد — با «صفر بار» یکی نیست. */
  used: number | null;
  lastUsed: string | null;
  /** بقیه‌ی ستون‌های ساده، به ترتیبِ `columns` در گزارش. */
  cells: Record<string, string>;
  /** همه‌ی متنِ ردیف، کوچک‌شده، برای جست‌وجو. */
  haystack: string;
  /** دو طرفِ جفت، خام؛ `b` برای جفتِ «اختیاری» `null` است. */
  pair: { a: string; b: string | null } | null;
  /** کلیدِ `crawler.alias_usage`؛ `null` اگر ستونش نیست. */
  rawHash: string | null;
  /** آدرس‌های کاملی که همین تفاوت را دارند، اول «دقیق»ها. */
  examples: AliasExample[];
};

export type AliasExample = {
  key: string | null;
  a: string;
  b: string;
  /**
   * `true` یعنی با همین یک جایگزینی دو آدرس عیناً یکی می‌شوند؛ `false` یعنی
   * این تفاوت در آن‌ها هست ولی جز آن فرقِ دیگری هم دارند.
   */
  exact: boolean;
};

export type AliasReport = {
  state: 'missing' | 'empty' | 'ready';
  total: number;
  shown: number;
  keyColumns: string[];
  /** اسمِ ستونی که حکم تشخیص داده شد، یا `null`. */
  verdictCol: string | null;
  deciderCol: string | null;
  /** کرالر شمارنده را اضافه کرده یا نه. */
  usageTracked: boolean;
  /** ستون‌های نمایشی — کلیدها اول، بعد بقیه به ترتیبِ الفبا. */
  columns: string[];
  byVerdict: { verdict: Verdict | 'none'; n: number }[];
  byDecider: { decider: Decider | 'none'; n: number }[];
  enabled: number;
  disabled: number;
  /** ردیف‌هایی که دست‌کم یک بار به کار رفته‌اند؛ `null` اگر شمارنده نیست. */
  usedRows: number | null;
  totalUses: number | null;
  orphanSwitches: number;
  /** فقط جفت‌ها — آدرس‌های کامل این‌جا نیستند. */
  rows: AliasRow[];
  /** چند ردیفِ «آدرسِ کامل» بود و چندتاشان زیرِ هیچ جفتی ننشستند. */
  fullValues: number;
  fullValuesUnmatched: number;
};

const isScalar = (v: unknown) =>
  v === null || ['string', 'number', 'boolean'].includes(typeof v);

/**
 * ستونی که همه‌ی مقدارهای پُرش در `allowed` است و دست‌کم یکی پُر است.
 * اگر چندتا بخوانند، آن که بیشترین مقدارِ پُر را دارد.
 */
export function detectColumn(
  rows: Record<string, unknown>[], allowed: readonly string[], skip: Set<string> = new Set(),
): string | null {
  const ok = new Set(allowed);
  const filled = new Map<string, number>();
  const bad = new Set<string>();
  for (const r of rows) {
    for (const [k, v] of Object.entries(r)) {
      if (skip.has(k) || bad.has(k) || v == null || v === '') continue;
      if (typeof v !== 'string' || !ok.has(v)) { bad.add(k); filled.delete(k); continue; }
      filled.set(k, (filled.get(k) ?? 0) + 1);
    }
  }
  let best: string | null = null;
  let bestN = 0;
  for (const [k, n] of [...filled.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
    if (!bad.has(k) && n > bestN) { best = k; bestN = n; }
  }
  return best;
}

const toNum = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const cellText = (v: unknown): string =>
  v == null ? '' : typeof v === 'string' ? v : String(v);

/**
 * متن برای تطبیق: کوچک، هر چیزِ جز حرف و رقم یک فاصله، و یک فاصله در دو سر —
 * تا `includes(' ct ')` فقط کلمه‌ی کامل را بگیرد و «ct» وسطِ «victoria» نه.
 */
export const matchText = (s: string): string => {
  const t = s.toLowerCase().normalize('NFKC').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  return t ? ` ${t} ` : '';
};

const squash = (s: string) => ` ${s.replace(/\s+/g, ' ').trim()} `;

/** همه‌ی جاهای `from` (کلمه‌ی کامل) در `text` با `to`؛ هر دو از `matchText`. */
const substitute = (text: string, from: string, to: string) =>
  squash(text.split(from).join(to || ' '));

/**
 * آیا دو آدرسِ کاملِ `x` و `y` همان تفاوتی را دارند که جفتِ `a`/`b`؟
 * `b` خالی یعنی جفتِ «اختیاری»: یک طرف `a` را دارد و دیگری ندارد.
 * همه‌ی ورودی‌ها از `matchText` آمده‌اند.
 *
 * `exact`: با همین یک جایگزینی دو آدرس عیناً یکی می‌شوند. وگرنه، اگر یک طرف
 * `a` دارد و دیگری نه (و برای جفتِ جایگزینی، دیگری `b` دارد و اولی نه)،
 * مثال هست ولی فرقِ دیگری هم در کار است.
 */
export function matchPair(a: string, b: string, x: string, y: string): 'exact' | 'loose' | null {
  if (!a || a === b || !x || !y || x === y) return null;
  for (const [p, q] of [[x, y], [y, x]]) {
    if (p.includes(a) && substitute(p, a, b) === squash(q)) return 'exact';
  }
  for (const [p, q] of [[x, y], [y, x]]) {
    if (!p.includes(a) || q.includes(a)) continue;
    if (!b || (q.includes(b) && !p.includes(b))) return 'loose';
  }
  return null;
}

/**
 * ⚠ ردیفی که شیء نیست، شیءِ خالی حساب می‌شود. یک بار (WHOSUP-266) SQL به‌جای
 * کلِ ردیف متنِ یک ستون را فرستاد و `'times_used' in "5 j"` کلِ سربرگ را با
 * TypeError سفید کرد؛ یک ردیفِ بد نباید بیش از همان ردیف را خراب کند.
 */
const asRow = (v: unknown): Record<string, unknown> =>
  v != null && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : {};

export function buildAliasReport(d: AliasEntriesData): AliasReport {
  const all = (d.rows ?? []).map((r) => ({ ...r, row: asRow(r.row) }));
  const allObjs = all.map((r) => r.row);

  const verdictCol = detectColumn(allObjs, VERDICTS);
  const deciderCol = detectColumn(allObjs, DECIDERS, new Set(verdictCol ? [verdictCol] : []));
  const usageTracked = allObjs.some((o) => USAGE_COL in o);

  // آدرس‌های کامل از فهرست بیرون می‌روند و فقط مثال‌اند.
  const isFull = (o: Record<string, unknown>) => o[FAMILY_COL] === FAMILY_FULL;
  const raw = all.filter((r) => !isFull(r.row));
  const full = all.filter((r) => isFull(r.row)).map((r) => ({
    key: r.key,
    a: cellText(r.row[SIDE_A_COL]),
    b: cellText(r.row[SIDE_B_COL]),
    ma: matchText(cellText(r.row[SIDE_A_COL])),
    mb: matchText(cellText(r.row[SIDE_B_COL])),
  }));
  const objs = raw.map((r) => r.row);
  const hasPairs = objs.some((o) => SIDE_A_COL in o);

  const hidden = new Set([USAGE_COL, LAST_USED_COL, 'enabled',
    ...(verdictCol ? [verdictCol] : []), ...(deciderCol ? [deciderCol] : []),
    // جفت ستونِ خودش را دارد؛ خانواده هم بعدِ جدا کردن همه‌جا «phrase» است.
    ...(hasPairs ? [SIDE_A_COL, SIDE_B_COL] : []), FAMILY_COL]);
  const seen = new Set<string>();
  for (const o of objs) {
    for (const [k, v] of Object.entries(o)) {
      // ستونِ jsonb/آرایه (شاهد، بافت) در یک خانه‌ی جدول خواندنی نیست.
      if (!hidden.has(k) && isScalar(v)) seen.add(k);
    }
  }
  // ستونی که یک جا شیء بوده، همه‌جا بیرون است — وگرنه یک ستون نصفه‌خالی می‌شود.
  for (const o of objs) for (const [k, v] of Object.entries(o)) if (!isScalar(v)) seen.delete(k);
  const keyCols = d.key_columns ?? [];
  const columns = [
    ...keyCols.filter((c) => seen.has(c)),
    ...[...seen].filter((c) => !keyCols.includes(c)).sort((a, b) => a.localeCompare(b)),
  ];

  const rows: AliasRow[] = raw.map((r) => {
    const o = r.row;
    const v = verdictCol ? o[verdictCol] : null;
    const dc = deciderCol ? o[deciderCol] : null;
    const cells: Record<string, string> = {};
    for (const c of columns) cells[c] = cellText(o[c]);
    // ستون‌های `a`/`b` خام نشان داده می‌شوند: یکسان‌شده‌شان «dr = drive» را
    // «dr» و «dr» می‌کرد و هیچ طرف «drive» نبود.
    if (SIDE_A_COL in o) {
      if (NORM_A_COL in cells) cells[NORM_A_COL] = cellText(o[SIDE_A_COL]);
      if (NORM_B_COL in cells) cells[NORM_B_COL] = cellText(o[SIDE_B_COL]);
    }
    const sideB = cellText(o[SIDE_B_COL]).trim();
    const pair = SIDE_A_COL in o
      ? { a: cellText(o[SIDE_A_COL]), b: sideB === '' || sideB === EMPTY_SIDE ? null : sideB }
      : null;
    const examples: AliasExample[] = [];
    if (pair) {
      const pa = matchText(pair.a);
      const pb = pair.b == null ? '' : matchText(pair.b);
      for (const f of full) {
        const m = matchPair(pa, pb, f.ma, f.mb);
        if (m) examples.push({ key: f.key, a: f.a, b: f.b, exact: m === 'exact' });
      }
      examples.sort((x, y) => Number(y.exact) - Number(x.exact) || x.a.localeCompare(y.a));
    }
    return {
      key: r.key,
      enabled: r.enabled !== false,
      switched: r.enabled != null,
      switchedAt: r.switched_at,
      verdict: (VERDICTS as readonly unknown[]).includes(v) ? (v as Verdict) : null,
      decider: (DECIDERS as readonly unknown[]).includes(dc) ? (dc as Decider) : null,
      used: usageTracked ? (toNum(o[USAGE_COL]) ?? 0) : null,
      lastUsed: usageTracked && o[LAST_USED_COL] ? String(o[LAST_USED_COL]) : null,
      cells,
      haystack: [pair?.a ?? '', pair?.b ?? '', ...Object.values(cells)].join(' ').toLowerCase(),
      pair,
      rawHash: o[RAW_HASH_COL] == null || o[RAW_HASH_COL] === '' ? null : String(o[RAW_HASH_COL]),
      examples,
    };
  });
  const matched = new Set(rows.flatMap((r) => r.examples.map((e) => e.key)));

  // پرکاربردها بالا؛ بی‌شمارنده، خاموش‌ها بالا تا دیده شوند.
  rows.sort((a, b) =>
    (b.used ?? 0) - (a.used ?? 0)
    || Number(a.enabled) - Number(b.enabled)
    || (a.key ?? '').localeCompare(b.key ?? ''));

  const count = <T,>(pick: (r: AliasRow) => T) => {
    const m = new Map<T, number>();
    for (const r of rows) m.set(pick(r), (m.get(pick(r)) ?? 0) + 1);
    return m;
  };
  const vm = count((r) => r.verdict ?? 'none');
  const dm = count((r) => r.decider ?? 'none');

  return {
    state: !d.exists ? 'missing' : (d.total ?? 0) === 0 ? 'empty' : 'ready',
    // هر دو شمارِ کلِ ردیف‌هاست، جفت و آدرسِ کامل با هم — فقط برای «به سقف خورد».
    total: d.total ?? all.length,
    shown: all.length,
    keyColumns: keyCols,
    verdictCol,
    deciderCol,
    usageTracked,
    columns,
    byVerdict: ([...VERDICTS, 'none'] as const).map((verdict) => ({ verdict, n: vm.get(verdict) ?? 0 })),
    byDecider: ([...DECIDERS, 'none'] as const).map((decider) => ({ decider, n: dm.get(decider) ?? 0 })),
    enabled: rows.filter((r) => r.enabled).length,
    disabled: rows.filter((r) => !r.enabled).length,
    usedRows: usageTracked ? rows.filter((r) => (r.used ?? 0) > 0).length : null,
    totalUses: usageTracked ? rows.reduce((s, r) => s + (r.used ?? 0), 0) : null,
    orphanSwitches: d.orphan_switches ?? 0,
    rows,
    fullValues: full.length,
    fullValuesUnmatched: full.filter((f) => !matched.has(f.key)).length,
  };
}

export type AliasFilter = {
  q?: string;
  verdict?: Verdict | 'none' | 'all';
  status?: 'all' | 'enabled' | 'disabled';
};

export function filterAliasRows(rows: AliasRow[], f: AliasFilter): AliasRow[] {
  const q = (f.q ?? '').trim().toLowerCase();
  return rows.filter((r) =>
    (!q || r.haystack.includes(q))
    && (!f.verdict || f.verdict === 'all' || (r.verdict ?? 'none') === f.verdict)
    && (!f.status || f.status === 'all' || (f.status === 'enabled') === r.enabled));
}
